/**
 * Reusable SVG fragment helpers for flat silhouettes.
 *
 * Each helper returns raw SVG markup (no `<g>` wrapper) that can be
 * interpolated directly into a silhouette function's template string.
 */
import { DETAIL_ATTRS, DETAIL_FILLED_ATTRS, STITCH_ATTRS } from "./_attrs.js";

import type { FlatView } from "../flat-svg.js";

/** Belt loops at given x positions. */
export function beltLoops(positions: number[], yTop: number, height: number): string {
  return positions
    .map((x) => `<rect x="${x}" y="${yTop}" width="6" height="${height}" rx="1" ${DETAIL_ATTRS}/>`)
    .join("\n  ");
}

/** Neck label (small tag at centre-back neckline). */
export function neckLabel(x: number, y: number, w = 20, h = 12): string {
  return `<rect x="${x - w / 2}" y="${y}" width="${w}" height="${h}" rx="1" ${DETAIL_ATTRS}/>`;
}

/** Double-row hem stitch. */
export function hemStitch(yTop: number, leftX: number, rightX: number, curve = 10): string {
  const midX = (leftX + rightX) / 2;
  return [
    `<path ${STITCH_ATTRS} d="M ${leftX + 1} ${yTop} Q ${midX} ${yTop + curve} ${rightX - 1} ${yTop}"/>`,
    `<path ${STITCH_ATTRS} d="M ${leftX} ${yTop + 6} Q ${midX} ${yTop + curve + 6} ${rightX} ${yTop + 6}"/>`,
  ].join("\n  ");
}

/** Vertical button placket (buttons along centre-front). */
export function placketButtons(cx: number, yPositions: number[]): string {
  return yPositions
    .map((y) => `<circle cx="${cx}" cy="${y}" r="4.5" ${DETAIL_FILLED_ATTRS}/>`)
    .join("\n  ");
}

/** Rib band (knit-style banded hem/cuff). */
export function ribBand(yTop: number, leftX: number, rightX: number, height: number): string {
  return [
    `<rect x="${leftX}" y="${yTop}" width="${rightX - leftX}" height="${height}" ${DETAIL_FILLED_ATTRS}/>`,
    `<path ${STITCH_ATTRS} d="M ${leftX + 6} ${yTop + 4} L ${rightX - 6} ${yTop + 4}"/>`,
    `<path ${STITCH_ATTRS} d="M ${leftX + 6} ${yTop + height - 4} L ${rightX - 6} ${yTop + height - 4}"/>`,
  ].join("\n  ");
}

/** Patch pocket (rectangle with topstitch). */
export function pocketPatch(x: number, y: number, w: number, h: number): string {
  return [
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" ${DETAIL_ATTRS}/>`,
    `<path ${STITCH_ATTRS} d="M ${x + 4} ${y + 4} L ${x + w - 4} ${y + 4} L ${x + w - 4} ${y + h - 4} L ${x + 4} ${y + h - 4} Z"/>`,
  ].join("\n  ");
}

/** Welt pocket (horizontal slit pocket). */
export function pocketWelt(x: number, y: number, w: number): string {
  return [
    `<path ${DETAIL_ATTRS} d="M ${x} ${y} L ${x + w} ${y}"/>`,
    `<path ${STITCH_ATTRS} d="M ${x + 2} ${y - 3} L ${x + w - 2} ${y - 3}"/>`,
    `<path ${STITCH_ATTRS} d="M ${x + 2} ${y + 3} L ${x + w - 2} ${y + 3}"/>`,
  ].join("\n  ");
}

/** Cargo pocket (large box with flap). */
export function pocketCargo(x: number, y: number, w: number, h: number): string {
  return [
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" ${DETAIL_ATTRS}/>`,
    `<path ${DETAIL_ATTRS} d="M ${x} ${y} L ${x + w} ${y} L ${x + w} ${y + 12} L ${x} ${y + 12} Z"/>`,
    `<path ${STITCH_ATTRS} d="M ${x + 3} ${y + 3} L ${x + w - 3} ${y + 3}"/>`,
    `<circle cx="${x + w / 2}" cy="${y + 8}" r="2.5" ${DETAIL_FILLED_ATTRS}/>`,
  ].join("\n  ");
}

/** Side stripe (track-pant style). */
export function sideStripe(leftX: number, rightX: number, yTop: number, yBot: number): string {
  return [
    `<path ${DETAIL_ATTRS} d="M ${leftX} ${yTop} L ${leftX - 8} ${yBot}"/>`,
    `<path ${DETAIL_ATTRS} d="M ${leftX + 6} ${yTop} L ${leftX - 2} ${yBot}"/>`,
    `<path ${DETAIL_ATTRS} d="M ${rightX} ${yTop} L ${rightX + 8} ${yBot}"/>`,
    `<path ${DETAIL_ATTRS} d="M ${rightX - 6} ${yTop} L ${rightX + 2} ${yBot}"/>`,
  ].join("\n  ");
}

/** Zip front (centre-front zipper). */
export function zipFront(yTop: number, yBot: number): string {
  const cx = 200;
  return [
    `<path ${DETAIL_ATTRS} d="M ${cx} ${yTop} L ${cx} ${yBot}"/>`,
    `<path ${STITCH_ATTRS} d="M ${cx - 4} ${yTop + 4} L ${cx - 4} ${yBot - 4}"/>`,
    `<path ${STITCH_ATTRS} d="M ${cx + 4} ${yTop + 4} L ${cx + 4} ${yBot - 4}"/>`,
    `<rect x="${cx - 5}" y="${yTop + 16}" width="10" height="14" rx="1.5" ${DETAIL_FILLED_ATTRS}/>`,
  ].join("\n  ");
}

/** Mandarin / band collar. */
export function mandarinCollar(view: FlatView, leftX: number, rightX: number, yNeck: number): string {
  const h = 18;
  if (view === "front") {
    return [
      `<path ${DETAIL_FILLED_ATTRS} d="M ${leftX} ${yNeck} Q ${(leftX + rightX) / 2} ${yNeck - 6} ${rightX} ${yNeck} L ${rightX} ${yNeck - h} Q ${(leftX + rightX) / 2} ${yNeck - h - 4} ${leftX} ${yNeck - h} Z"/>`,
      `<path ${STITCH_ATTRS} d="M ${leftX + 4} ${yNeck - 4} Q ${(leftX + rightX) / 2} ${yNeck - 10} ${rightX - 4} ${yNeck - 4}"/>`,
    ].join("\n  ");
  }
  return [
    `<path ${DETAIL_FILLED_ATTRS} d="M ${leftX} ${yNeck} L ${rightX} ${yNeck} L ${rightX} ${yNeck - h} Q ${(leftX + rightX) / 2} ${yNeck - h - 6} ${leftX} ${yNeck - h} Z"/>`,
    `<path ${STITCH_ATTRS} d="M ${leftX + 4} ${yNeck - 4} L ${rightX - 4} ${yNeck - 4}"/>`,
  ].join("\n  ");
}

/** Drawcords (hoodie/jogger waist). */
export function drawcords(cx: number, y: number): string {
  return [
    `<path ${DETAIL_ATTRS} d="M ${cx - 8} ${y} L ${cx - 12} ${y + 18}"/>`,
    `<path ${DETAIL_ATTRS} d="M ${cx + 8} ${y} L ${cx + 12} ${y + 18}"/>`,
    `<rect x="${cx - 15}" y="${y + 16}" width="6" height="10" rx="1.5" ${DETAIL_FILLED_ATTRS}/>`,
    `<rect x="${cx + 9}" y="${y + 16}" width="6" height="10" rx="1.5" ${DETAIL_FILLED_ATTRS}/>`,
  ].join("\n  ");
}

/** Elastic gather (waist or cuff). */
export function elasticGather(yCenter: number, leftX: number, rightX: number): string {
  const count = Math.floor((rightX - leftX) / 12);
  const step = (rightX - leftX) / count;
  const parts: string[] = [];
  for (let i = 0; i < count; i++) {
    const x1 = leftX + i * step;
    const x2 = x1 + step;
    const mid = (x1 + x2) / 2;
    parts.push(`M ${x1} ${yCenter} Q ${mid} ${yCenter - 4} ${x2} ${yCenter}`);
  }
  return `<path ${STITCH_ATTRS} d="${parts.join(" ")}"/>`;
}

/** Yoke seam (horizontal seam across upper back). */
export function yokeSeam(y: number, leftX: number, rightX: number): string {
  const midX = (leftX + rightX) / 2;
  return [
    `<path ${DETAIL_ATTRS} d="M ${leftX} ${y} Q ${midX} ${y + 10} ${rightX} ${y}"/>`,
    `<path ${STITCH_ATTRS} d="M ${leftX + 2} ${y + 4} Q ${midX} ${y + 14} ${rightX - 2} ${y + 4}"/>`,
  ].join("\n  ");
}

/** Sleeve cuff band (rectangular cuff at wrist). */
export function sleeveCuff(x: number, y: number, w: number, h: number): string {
  return [
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" ${DETAIL_FILLED_ATTRS}/>`,
    `<path ${STITCH_ATTRS} d="M ${x + 2} ${y + h / 2} L ${x + w - 2} ${y + h / 2}"/>`,
  ].join("\n  ");
}

/** Waistband (simple rectangular band). */
export function waistband(leftX: number, rightX: number, yTop: number, height: number): string {
  return [
    `<path ${DETAIL_ATTRS} d="M ${leftX} ${yTop + height} L ${rightX} ${yTop + height}"/>`,
    `<path ${STITCH_ATTRS} d="M ${leftX + 2} ${yTop + 4} L ${rightX - 2} ${yTop + 4}"/>`,
    `<path ${STITCH_ATTRS} d="M ${leftX + 2} ${yTop + height - 4} L ${rightX - 2} ${yTop + height - 4}"/>`,
  ].join("\n  ");
}

/** Wrap/tie belt. */
export function wrapBelt(cx: number, y: number, halfWidth: number): string {
  return [
    `<path ${DETAIL_ATTRS} d="M ${cx - halfWidth} ${y} L ${cx + halfWidth} ${y}"/>`,
    `<path ${DETAIL_ATTRS} d="M ${cx - halfWidth} ${y + 8} L ${cx + halfWidth} ${y + 8}"/>`,
    `<path ${DETAIL_ATTRS} d="M ${cx + halfWidth - 4} ${y} L ${cx + halfWidth + 16} ${y + 18}"/>`,
    `<path ${DETAIL_ATTRS} d="M ${cx + halfWidth} ${y} L ${cx + halfWidth + 20} ${y + 14}"/>`,
  ].join("\n  ");
}

/** Shoulder seam stitch lines. */
export function shoulderSeams(leftArmpit: number, rightArmpit: number, yArmpit: number, shoulderY: number): string {
  return [
    `<path ${STITCH_ATTRS} d="M ${leftArmpit} ${yArmpit} Q ${leftArmpit + 10} ${shoulderY + 10} ${leftArmpit + 30} ${shoulderY}"/>`,
    `<path ${STITCH_ATTRS} d="M ${rightArmpit} ${yArmpit} Q ${rightArmpit - 10} ${shoulderY + 10} ${rightArmpit - 30} ${shoulderY}"/>`,
  ].join("\n  ");
}
